import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box } from 'ui/bend'
import { GlobalStyle } from 'ui/chandra'
import { useToggleFocus } from 'hooks'
import EntryProviders from './EntryProviders'
import Header from '../Header'
import Footer, { footerHeight } from '../Footer'
import IE, { isIE } from '../IE'
import { useAuthFeedback } from '../AuthProviders'
import ToastContainer from 'ui/bend/blocks/ToastContainer'
import { LINK_SOURCE, showHeaderAndFooterWebView } from 'components_v2/common'
import styled from 'styled-components'
import CommonLoader, { useGlobalLoader } from 'components_v2/CommonLoader'
import NotFoundPage from 'components_v2/NotFound/NotFoundPage'
import NotAvailable from './grouped/NotAvailable'
import HandleNewUserPopup from './HandleNewUserPopup'

const Main = styled(Box)`
  &.page-loading {
    height: 0;
    overflow: hidden;
    visibility: hidden;
  }
  * {
    outline: none;
  }
`

/****************************************
/* TODO: React Refactor
/*  This is the temporary entry point for redesigned pages.
/*  Once we switch over to full React, we can do away with this page.
****************************************/

// Display controls
const defaultControls = {
  alert: true,
  navbar: true,
  navbarLinks: true, // Links, hoverables, search, etc.
  navbarAuth: true,
  footer: true,
  footerLinks: true,
  footerFlush: {
    mobile: false,
    desktop: false
  }
}

const NotFoundContainer = styled.div`
  * {
    color: black;
  }
`

export const TempEntry = ({
  loadUser,
  user,
  controls,
  deprecated,
  children,
  notAvailable = false,
  notFound = false,
  ...rest
}) => {
  const localControls = {
    ...defaultControls,
    ...controls
  }
  const globalLoading = useGlobalLoader()
  const showLinks =
    LINK_SOURCE !== 'iosApp' &&
    showHeaderAndFooterWebView({
      iosAppClientId: rest?.iosAppClientId,
      iosAppScope: rest?.iosAppScope
    })
  const loading = globalLoading && showLinks
  // Use a block body so we don't return scrollTo's value as the effect
  // cleanup. As of Chrome 150 window.scrollTo() returns a Promise (previously
  // undefined); returning it makes React call it as `destroy()` on re-run,
  // throwing "destroy is not a function" and blanking the page.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [loading])

  useAuthFeedback()
  useToggleFocus()
  const noData = notFound || notAvailable

  const Child = () => {
    if (loading) return <CommonLoader />
    if (notFound)
      return (
        <NotFoundContainer>
          <NotFoundPage />
        </NotFoundContainer>
      )
    if (notAvailable) return <NotAvailable />
    return null
  }

  return (
    <EntryProviders user={user} loadUser={loadUser}>
      <GlobalStyle />
      {showLinks && <Header controls={localControls} />}
      <Child />
      {!user && <HandleNewUserPopup />}
      <Main
        mt={['0px', null, '64px']}
        pb={
          localControls.footer
            ? footerHeight({
                pad: true,
                links: localControls.footerLinks,
                flush: localControls.footerFlush
              })
            : '0px'
        }
        {...((loading || noData) && {
          className: 'page-loading'
        })}
        {...rest}
      >
        {noData ? <></> : <>{isIE ? <IE /> : { ...children }}</>}
      </Main>
      {localControls.footer && showLinks && (
        <Footer
          links={localControls.footerLinks}
          flush={localControls.footerFlush}
        />
      )}
      <ToastContainer />
    </EntryProviders>
  )
}

TempEntry.defaultProps = {
  // See defaultControls above
  controls: {}
}

TempEntry.propTypes = {
  loadUser: PropTypes.bool,
  user: PropTypes.object,
  controls: PropTypes.object,
  children: PropTypes.node
}

export default TempEntry
